import { Rng } from '../rng';
import type { Bathymetry } from '../map/bathymetry';
import {
  buildNavGrid,
  findRoute,
  directRoute,
  type CoastStrand,
  type NavGrid,
  type Route,
} from '../map/navigation';
import { HOME_BASE } from '../world/places';
import { distanceNm, type LatLon } from '../geo';
import { createTutorialState, type TutorialState } from '../tutorial/tutorial';
import { generateIncident, type Incident } from '../mission/incident';
import {
  outcomeFor,
  forecastTier,
  CLUES_PER_TIER,
  EVIDENCE_KINDS_PER_TIER,
  REPUTATION_PER_TIER,
  type OutcomeTier,
  type TierForecast,
} from '../mission/outcome';
import { createWeather, type WeatherField } from '../voyage/weather';
import { Voyage } from '../voyage/voyage';
import type { SpeedSetting } from '../voyage/profiles';
import { planVoyageEvents, type VoyageEvent } from '../voyage/events';
import { RESEARCH_SHIP, SUBMARINE, type VesselId, type VesselProfile } from '../voyage/profiles';
import { generateCase, toCollectedClue, type CaseBeat, type MysteryCase } from '../mystery/case';
import { EvidenceLedger, type CollectedClue } from '../mystery/ledger';
import { SKELETON, TOTAL_BEATS, type Act } from '../mystery/skeleton';
import type { MysteryId } from '../mystery/types';
import { backgroundEffects, createCharacter, type Character } from '../character/character';

/**
 * Der Zustand eines Durchlaufs.
 *
 * Haelt alles zusammen, was M2 und M3 verbindet: die vergehende Zeit, die
 * eintreffenden Notrufe, den Zustand der beiden Fahrzeuge und den Fortschritt
 * durch das Erzaehlskelett.
 *
 * Der gesamte Durchlauf haengt an einem Seed. Damit ist er reproduzierbar —
 * Voraussetzung sowohl fuer den Kohaerenztest als auch fuer nachstellbare
 * Fehlerberichte aus dem fertigen Spiel.
 */

/**
 * Wie oft im Mittel ein Notruf eintrifft, in Stunden.
 *
 * Frueher 26 bis 52 Stunden. Gemessen ergab das in den ersten vierundzwanzig
 * Stunden **immer hoechstens einen** offenen Notruf — also gar keine Wahl,
 * sondern eine Warteschlange. Das Spiel handelt davon, dass man nicht alles
 * beantworten kann; dafuer muss mehr als eines dastehen.
 */
const SPAWN_INTERVAL_HOURS: readonly [number, number] = [14, 30];

/** So viele Notrufe stehen hoechstens gleichzeitig an. */
export const MAX_ACTIVE_INCIDENTS = 6;

/**
 * So viele Notrufe stehen schon, wenn die Kampagne beginnt.
 *
 * Ohne sie war der Anfang zwangslaeufig Einzelverkehr: ein Ruf, eine Fahrt,
 * zurueck, warten. Die erste Entscheidung des Spiels soll aber eine
 * Entscheidung sein.
 */
const INITIAL_INCIDENTS = 3;

export interface VesselCondition {
  fuel: number;
  hull: number;
  /**
   * Wo das Fahrzeug gerade steht.
   *
   * Ohne dieses Feld begann jede Fahrt an der Station, und die eigene Position
   * war strategisch bedeutungslos. Jetzt bleibt ein Schiff nach dem Einsatz
   * dort, wo es war: Der naechste Notruf laesst sich von See aus annehmen —
   * schneller, aber ohne Nachschub, ohne Werft und ohne Speicherpunkt.
   */
  position: LatLon;
}

/** Innerhalb dieses Umkreises gilt ein Fahrzeug als an der Station. */
export const AT_BASE_RADIUS_NM = 3;

/**
 * Wie lange ein Notruf braucht, bis er *bei Ihnen* ankommt.
 *
 * Bis M9 hatte das Ansehen keinerlei Wirkung — es wurde verrechnet, angezeigt
 * und ignoriert. Jetzt entscheidet es darueber, wie frueh das Institut von
 * einem Vorfall erfaehrt: Wer einen Ruf hat, dem funken Kuestenwache und
 * Handelsschiffe zuerst; wer keinen hat, liest es hinterher im Lagebericht.
 *
 * Die Stunden fehlen dann genau dort, wo es weh tut — im Rettungsfenster. Das
 * ist eine Rueckkopplung, und deshalb ist sie nach beiden Seiten gedeckelt:
 * Ein schlechter Lauf soll spuerbar sein, aber nicht in eine Abwaertsspirale
 * fuehren, aus der man nicht mehr herauskommt.
 */
export const REPORT_DELAY_HOURS: Readonly<{ best: number; worst: number }> = {
  best: 0,
  worst: 14,
};

/** Ab diesem Ansehen erfaehrt man alles sofort. */
const STANDING_FOR_INSTANT_REPORT = 60;

/** Bei diesem Ansehen dauert es am laengsten. */
const STANDING_AT_WORST = -20;

/**
 * Wie viele Stunden vergehen, bis ein Vorfall gemeldet wird.
 *
 * Ausserhalb der Spanne wird geklemmt — sonst waere ein sehr gutes oder sehr
 * schlechtes Ansehen irgendwann absurd.
 */
export function reportDelayHours(reputation: number): number {
  const span = STANDING_FOR_INSTANT_REPORT - STANDING_AT_WORST;
  const t = Math.max(0, Math.min(1, (reputation - STANDING_AT_WORST) / span));
  return REPORT_DELAY_HOURS.worst + t * (REPORT_DELAY_HOURS.best - REPORT_DELAY_HOURS.worst);
}

export interface MissionResult {
  readonly incident: Incident;
  readonly tier: OutcomeTier;
  readonly elapsedHours: number;
  readonly souls: number;
  readonly rescued: number;
  readonly clues: readonly CollectedClue[];
  readonly reputationDelta: number;
  /** Lag der Fundort tiefer, als das Fahrzeug arbeiten kann? */
  readonly beyondWorkingDepth: boolean;
}

/**
 * Fassung des Speicherformats. Aeltere Staende werden verworfen statt migriert.
 *
 * 2: Portraetkennung statt Merkmalswerten, Fahrzeugpositionen, Tutorialstand.
 */
export const SAVE_VERSION = 2;

export interface CampaignSave {
  readonly version: number;
  readonly seed: string;
  readonly character: Character;
  readonly clockHours: number;
  readonly collected: readonly number[];
  readonly reputation: number;
  readonly missionsFlown: number;
  readonly soulsRescued: number;
  readonly soulsLost: number;
  readonly incidentCounter: number;
  readonly nextSpawnAt: number;
  readonly active: readonly Incident[];
  readonly vessels: Readonly<Record<VesselId, VesselCondition>>;
  readonly tutorial: TutorialState;
}

/**
 * Ein Fahrzeug, das gerade unterwegs ist.
 *
 * Bis M9 gab es hoechstens eine Fahrt, und sie lebte in der Kartenszene. Damit
 * war das zweite Fahrzeug Kulisse: Man konnte es zwar an einen anderen Ort
 * schicken, aber nie gleichzeitig einsetzen. Genau daran fehlte dem Spiel die
 * eigentliche Entscheidung — zwei Schiffe, fuenf Notrufe, verschiedene
 * Reichweiten und Tiefgaenge.
 *
 * Deshalb liegen die Fahrten jetzt hier, je Fahrzeug eine. Die Kartenszene
 * stellt sie nur noch dar.
 */
export interface ActiveMission {
  readonly vesselId: VesselId;
  /** `null` auf der Heimfahrt. */
  readonly incident: Incident | null;
  readonly homebound: boolean;
  readonly voyage: Voyage;
  readonly plannedEvents: readonly VoyageEvent[];
  /** Wie viele der geplanten Ereignisse schon vorgekommen sind. */
  nextEventIndex: number;
  /** Unterwegs untersuchte Sonarkontakte. */
  divertedFindings: number;
  /** Unterwegs gerettete Menschen. */
  divertedRescues: number;
}

/**
 * Ein Notruf, den man hat verstreichen lassen.
 *
 * Bis M9 verschwand er einfach aus der Liste — die einzige Stelle, an der das
 * Spiel seine eigene Haerte wegdrueckte. Man liess Menschen sterben und
 * bekam dafuer eine kuerzere Liste.
 *
 * Jetzt wird jeder aufgehoben und gemeldet. Die Zahl der Verlorenen zaehlte
 * ohnehin schon in die Bilanz des Finales; neu ist, dass man es *erfaehrt*,
 * mit Namen, waehrend es passiert.
 */
export interface LapsedIncident {
  readonly incident: Incident;
  /** Kampagnenzeit, zu der die letzte Frist ablief. */
  readonly lapsedAtHours: number;
}

export interface Verdict {
  readonly accusation: MysteryId;
  readonly truth: MysteryId;
  readonly correct: boolean;
  readonly supported: boolean;
  readonly cluesCollected: number;
  readonly cluesPossible: number;
  readonly rescued: number;
  readonly lost: number;
  /** Wurde die Spur des eigenen Verlusts geborgen? */
  readonly personalAnswered: boolean;
}

export class Campaign {
  readonly seed: string;
  readonly character: Character;
  readonly mysteryCase: MysteryCase;
  readonly ledger = new EvidenceLedger();
  readonly weather: WeatherField;

  private readonly bathymetry: Bathymetry;
  /**
   * Die gezeichneten Kuestenzuege.
   *
   * Sie gehen in das Navigationsraster ein, damit der gefahrene Kurs und die
   * gezeichnete Kueste dieselbe Wahrheit benutzen. Ohne sie faehrt das Schiff
   * ueber Land — nicht aus Versehen, sondern weil das Hoehenraster dort
   * tatsaechlich Wasser meldet.
   */
  private readonly coastline: readonly CoastStrand[];
  private readonly incidentRng: Rng;

  /**
   * Navigationsraster je Tiefgang. Der Aufbau kostet einmalig etwas Zeit,
   * deshalb wird er behalten — Schiff und U-Boot brauchen je eines.
   */
  private readonly navGrids = new Map<number, NavGrid>();

  private clock = 0;
  /** Indizes der bereits geborgenen Beats. */
  private readonly collected = new Set<number>();
  private incidentCounter = 0;
  private nextSpawnAt: number;
  private active: Incident[] = [];
  /** Verstrichene Notrufe, die noch gemeldet werden muessen. */
  private lapsed: LapsedIncident[] = [];
  private reputationTotal = 0;
  private missionsFlown = 0;
  /** Bilanz der Kampagne — sie steht am Ende neben der Aufloesung. */
  private soulsRescued = 0;
  private soulsLost = 0;

  readonly vessels: Record<VesselId, VesselCondition> = {
    ship: { fuel: RESEARCH_SHIP.fuelCapacity, hull: 100, position: { ...HOME_BASE.position } },
    submarine: { fuel: SUBMARINE.fuelCapacity, hull: 100, position: { ...HOME_BASE.position } },
  };

  /** Fortschritt der Einweisung. Gehoert in den Spielstand, sonst begaenne
   * das Tutorial nach jedem Fortsetzen von vorn. */
  readonly tutorial: TutorialState = createTutorialState();

  /**
   * Die laufenden Fahrten, je Fahrzeug hoechstens eine.
   *
   * Bewusst nicht im Spielstand: Eine angefangene Fahrt liesse sich zwar
   * speichern, aber das Spiel soll auf See keinen Ruecksetzpunkt haben. Wer
   * mitten auf dem Weg neu laedt, steht wieder dort, wo er abgelegt hat.
   */
  private readonly missions: Partial<Record<VesselId, ActiveMission>> = {};

  constructor(params: {
    seed: string;
    bathymetry: Bathymetry;
    character?: Character;
    coastline?: readonly CoastStrand[];
  }) {
    this.seed = params.seed;
    this.bathymetry = params.bathymetry;
    this.coastline = params.coastline ?? [];

    const root = Rng.fromSeed(params.seed);
    this.character = params.character ?? createCharacter(root.derive('character'));
    this.mysteryCase = generateCase(root);
    this.weather = createWeather(`${params.seed}::weather`);
    this.incidentRng = root.derive('incidents');

    // Das Spiel beginnt mit einer Lage, nicht mit einer leeren Liste.
    this.nextSpawnAt = this.incidentRng.float(2, 8);
    for (let i = 0; i < INITIAL_INCIDENTS; i++) {
      // Gestaffelt in die juengste Vergangenheit gelegt: So sind die Fenster
      // unterschiedlich weit fortgeschritten und die Wahl faellt schwerer.
      this.spawnIncident(-this.incidentRng.float(2, 20));
    }
    this.advanceTo(0);
  }

  get clockHours(): number {
    return this.clock;
  }

  get reputation(): number {
    return this.reputationTotal;
  }

  /**
   * Wie lange es dauert, bis ein neuer Notruf hier ankommt.
   *
   * Fuer die Anzeige — der Spieler soll sehen, was sein Ansehen ihm einbringt
   * oder kostet, und nicht raten muessen.
   */
  get reportDelay(): number {
    return reportDelayHours(this.reputationTotal);
  }

  /**
   * Setzt das Ansehen unmittelbar — nur fuer Tests.
   *
   * Der normale Weg dorthin fuehrt ueber Missionen, und die haengen am Seed.
   * Um die *Wirkung* des Ansehens zu pruefen, braucht es aber zwei Laeufe, die
   * sich in nichts anderem unterscheiden.
   */
  applyReputationForTest(value: number): void {
    this.reputationTotal = value;
  }

  /** Faktor auf Werft- und Bunkerzeiten. Kleiner ist besser. */
  get refitFactor(): number {
    // Dieselbe Spanne wie beim Meldeverzug, damit beides an derselben Zahl
    // haengt und sich nicht widerspricht.
    const t = Math.max(0, Math.min(1, (this.reputationTotal + 20) / 80));
    return 1.5 - t;
  }

  get missionCount(): number {
    return this.missionsFlown;
  }

  /**
   * Wie viele Menschen geborgen wurden und wie viele nicht.
   *
   * Das ist die Zahl, die im Finale neben der Wahrheit steht: Der Fall wird
   * nur geloest, indem man Notrufe verstreichen laesst — und diese Bilanz
   * haelt fest, was das gekostet hat.
   */
  get tally(): { rescued: number; lost: number } {
    return { rescued: this.soulsRescued, lost: this.soulsLost };
  }

  get cluesCollected(): number {
    return this.collected.size;
  }

  get cluesPossible(): number {
    return TOTAL_BEATS;
  }

  /**
   * Der Akt, in dem der Fall gerade steht.
   *
   * Ein Akt schliesst erst, wenn *alle* seine Beats geborgen sind. Wer nur
   * rettet, bleibt in Akt I haengen, obwohl er fleissig Hinweise sammelt —
   * das ist die beabsichtigte Reibung.
   */
  get currentAct(): Act {
    for (const act of [1, 2, 3] as const) {
      const open = SKELETON.some((b) => b.act === act && !this.collected.has(b.index));
      if (open) return act;
    }
    return 3;
  }

  /** Sind alle Hinweise geborgen? Dann kann die Anklage erhoben werden. */
  get isReadyForFinale(): boolean {
    return this.collected.size >= TOTAL_BEATS;
  }

  /**
   * Laesst die Kampagnenzeit bis zum angegebenen Zeitpunkt vergehen und
   * erzeugt dabei neue Notrufe. Abgelaufene Vorfaelle werden aussortiert.
   */
  advanceTo(hours: number): void {
    if (hours > this.clock) this.clock = hours;

    /*
     * Aussortieren und Nachruecken muessen sich abwechseln.
     *
     * Frueher lief die Schleife komplett durch und *danach* wurde aussortiert.
     * Sprang viel Zeit auf einmal — eine lange Ueberfahrt, eine Werftliegezeit —,
     * fuellte sich die Liste mit vier laengst vergangenen Vorfaellen, blockierte
     * damit jedes weitere Nachruecken, und am Ende flogen alle vier gleichzeitig
     * hinaus. Gemessen: nach 720 Stunden stand im Mittel **kein einziger**
     * Notruf mehr an. Die Kampagne versiegte.
     */
    while (this.nextSpawnAt <= this.clock) {
      this.pruneExpiredAt(this.nextSpawnAt);
      if (this.active.length < MAX_ACTIVE_INCIDENTS) {
        this.spawnIncident(this.nextSpawnAt);
      }
      this.nextSpawnAt += this.incidentRng.float(
        SPAWN_INTERVAL_HOURS[0],
        SPAWN_INTERVAL_HOURS[1],
      );
    }

    this.pruneExpired();
  }

  /**
   * Laesst Zeit vergehen, ohne dass sich etwas bewegt.
   *
   * An der Station heisst das warten, auf See heisst es vor Anker liegen. Der
   * Unterschied ist keiner der Mechanik — Treibstoff wird in beiden Faellen
   * keiner verbraucht —, sondern einer der Lage: Wer draussen bleibt, steht
   * naeher am naechsten Notruf und weiter weg von jeder Werft.
   */
  wait(hours: number): void {
    this.advanceTo(this.clock + Math.max(0, hours));
  }

  private spawnIncident(atHours: number): void {
    const id = `incident-${this.incidentCounter++}`;
    const seaState = this.weather.seaStateAt(HOME_BASE.position, atHours);

    /*
     * Der Vorfall ist schon passiert, wenn Sie davon erfahren.
     *
     * Wie lange vorher, haengt am Ansehen des Instituts: Wer einen Ruf hat,
     * dem funken Kuestenwache und Handelsschiffe zuerst. Die Stunden fehlen
     * hinterher genau dort, wo es weh tut.
     */
    const delay = reportDelayHours(this.reputationTotal);

    const incident = generateIncident({
      rng: this.incidentRng.derive(id),
      bathymetry: this.bathymetry,
      occurredAtHours: atHours - delay,
      seaState,
      id,
    });

    if (incident !== null) this.active.push(incident);
  }

  /** Vorfaelle, bei denen selbst eine kalte Spur nicht mehr zu holen ist, fallen weg. */
  private pruneExpired(): void {
    this.pruneExpiredAt(this.clock);
  }

  /** Dasselbe, aber zu einem gedachten Zeitpunkt — fuer das Nachruecken. */
  private pruneExpiredAt(hours: number): void {
    const kept: Incident[] = [];

    for (const incident of this.active) {
      if (hours - incident.occurredAtHours <= incident.windows.coldHours) {
        kept.push(incident);
        continue;
      }

      // Wer verstreicht, verschwindet nicht stillschweigend.
      this.lapsed.push({ incident, lapsedAtHours: hours });
      this.soulsLost += incident.souls;
    }

    this.active = kept;
  }

  /**
   * Notrufe, die seit dem letzten Abruf verstrichen sind.
   *
   * Wird geleert, wenn man sie abholt: Die Meldung soll einmal kommen, nicht
   * bei jedem Neuzeichnen wieder.
   */
  takeLapsed(): readonly LapsedIncident[] {
    const out = this.lapsed;
    this.lapsed = [];
    return out;
  }

  get activeIncidents(): readonly Incident[] {
    return this.active;
  }

  /** Wie alt ein Vorfall gerade ist, in Stunden. */
  ageOf(incident: Incident): number {
    return this.clock - incident.occurredAtHours;
  }

  /** Wo ein Fahrzeug gerade steht. */
  positionOf(vesselId: VesselId): LatLon {
    return this.vessels[vesselId].position;
  }

  /** Liegt das Fahrzeug an der Station? Nur dort gibt es Nachschub und Werft. */
  isAtBase(vesselId: VesselId): boolean {
    return distanceNm(this.positionOf(vesselId), HOME_BASE.position) <= AT_BASE_RADIUS_NM;
  }

  /**
   * Entfernung zum Vorfall — Luftlinie, fuer die Uebersicht.
   *
   * Gemessen wird ab dem Fahrzeug, nicht ab der Station: Wer auf See steht,
   * soll sehen, was von *dort* erreichbar ist. Das ist der ganze Sinn daran,
   * draussen zu bleiben.
   */
  distanceTo(incident: Incident, vesselId: VesselId): number {
    return distanceNm(this.positionOf(vesselId), incident.position);
  }

  /** Entfernung von einem Fahrzeug zurueck zur Station. */
  distanceHome(vesselId: VesselId): number {
    return distanceNm(this.positionOf(vesselId), HOME_BASE.position);
  }

  /**
   * Der befahrbare Kurs zu einem Vorfall, ab der aktuellen Position des
   * Fahrzeugs.
   *
   * Findet die Wegsuche nichts — etwa weil das Ziel in einer eingeschlossenen
   * Lagune liegt —, wird auf die Luftlinie zurueckgefallen. Lieber eine
   * unsaubere Route als eine Mission, die sich nicht anfliegen laesst.
   */
  routeTo(incident: Incident, draughtMetres: number, from: LatLon): Route {
    return this.routeBetween(from, incident.position, draughtMetres);
  }

  /** Der Kurs zurueck zur Station. */
  routeHome(vesselId: VesselId, draughtMetres: number): Route {
    return this.routeBetween(this.positionOf(vesselId), HOME_BASE.position, draughtMetres);
  }

  private routeBetween(from: LatLon, to: LatLon, draughtMetres: number): Route {
    let grid = this.navGrids.get(draughtMetres);
    if (grid === undefined) {
      grid = buildNavGrid(this.bathymetry, draughtMetres, undefined, {
        coastline: this.coastline,
      });
      this.navGrids.set(draughtMetres, grid);
    }

    return findRoute(grid, from, to) ?? directRoute(from, to);
  }

  /**
   * Was bei Ankunft nach `arrivalHours` **tatsaechlich** herauskaeme.
   * Nur fuer die Auswertung und fuer Tests — nie fuer Anzeigen.
   */
  tierOnArrival(incident: Incident, arrivalHours: number): OutcomeTier {
    return outcomeFor(arrivalHours - incident.occurredAtHours, incident.windows);
  }

  /**
   * Was die Station bei Ankunft nach `arrivalHours` *erwartet*.
   *
   * Das ist der Wert, den alle Anzeigen benutzen. Er kann danebenliegen —
   * absichtlich. Ohne diese Ungewissheit waere jede Missionswahl eine
   * Rechenaufgabe mit bekanntem Ergebnis.
   */
  forecastOnArrival(
    incident: Incident,
    arrivalHours: number,
    vesselId?: VesselId,
  ): TierForecast {
    const effects = backgroundEffects(this.character.background);
    const forecast = forecastTier(
      arrivalHours - incident.occurredAtHours,
      incident.estimatedWindows,
      incident.estimateUncertaintyHours * effects.forecastUncertaintyFactor(incident.kind),
    );

    if (vesselId === undefined) return forecast;

    /*
     * Was das Fahrzeug ueberhaupt heben kann, gehoert in die Vorhersage.
     *
     * Ohne das versprach die Einsatzplanung eine Bergung, die das
     * Forschungsschiff in 5.000 m Tiefe gar nicht leisten kann — und der
     * Spieler erfuhr es erst am Fundort. Die Prognose darf unsicher sein, aber
     * sie darf nicht etwas zusagen, das ausgeschlossen ist.
     */
    const profile = vesselId === 'ship' ? RESEARCH_SHIP : SUBMARINE;
    if (incident.depthMetres <= profile.maxWorkingDepthMetres) return forecast;

    const capped = (tier: OutcomeTier): OutcomeTier =>
      tier === 'salvage' ? 'cold' : tier;

    return {
      ...forecast,
      tier: capped(forecast.tier),
      alternative:
        forecast.alternative === null || capped(forecast.alternative) === capped(forecast.tier)
          ? null
          : capped(forecast.alternative),
    };
  }

  /**
   * Schliesst eine Mission ab.
   *
   * Der Zeitpunkt der Ankunft entscheidet ueber die Stufe, die Stufe ueber die
   * Zahl der Hinweise. Diese werden dem Erzaehlskelett der Reihe nach
   * entnommen — der Fall schreitet also genau so weit voran, wie der Spieler
   * ihn vorantreibt.
   */
  resolveMission(params: {
    incident: Incident;
    arrivalHours: number;
    vessel: VesselProfile;
    fuelLeft: number;
    hullLeft: number;
    /** Unterwegs untersuchte Sonarkontakte — sie liefern Anomalie-Signaturen. */
    divertedFindings?: number;
    /** Unterwegs gerettete Menschen, etwa nach einem Zweitnotruf. */
    divertedRescues?: number;
  }): MissionResult {
    const { incident, arrivalHours, vessel } = params;

    this.advanceTo(arrivalHours);
    // Das Fahrzeug bleibt am Einsatzort liegen. Von hier aus geht es entweder
    // heim oder gleich weiter — beides ist eine Entscheidung, keine Formalie.
    this.vessels[vessel.id] = {
      fuel: params.fuelLeft,
      hull: params.hullLeft,
      position: { ...incident.position },
    };

    const elapsed = arrivalHours - incident.occurredAtHours;
    let tier = outcomeFor(elapsed, incident.windows);

    // Zu tief fuer das gewaehlte Fahrzeug: Man sieht das Wrack, kommt aber
    // nicht heran. Aus einer Bergung wird bestenfalls eine kalte Spur.
    const beyondWorkingDepth = incident.depthMetres > vessel.maxWorkingDepthMetres;
    if (beyondWorkingDepth && tier !== 'nothing' && tier !== 'rescue') {
      tier = 'cold';
    }

    // Die Meeresbiologin liest die Umgebung — kalte Spuren geben ihr mehr her.
    const effects = backgroundEffects(this.character.background);
    const clueCount =
      CLUES_PER_TIER[tier] + (tier === 'cold' ? effects.coldTrailBonusClues : 0);
    const clues = this.awardClues(clueCount, incident, tier);

    // Was unterwegs untersucht wurde, zaehlt unabhaengig vom Ergebnis am Ziel:
    // Die Anomalie war da, ob am Wrack noch jemand lebte oder nicht.
    const enRoute = this.awardCluesOfKind(
      params.divertedFindings ?? 0,
      incident,
      'anomaly_signature',
    );
    const reputationDelta = REPUTATION_PER_TIER[tier];
    this.reputationTotal += reputationDelta;
    this.missionsFlown++;

    if (tier === 'rescue') this.soulsRescued += incident.souls;
    else this.soulsLost += incident.souls;

    // Wer unterwegs abgedreht hat, hat diese Menschen ebenfalls gerettet —
    // und dafuer womoeglich am Ziel zu spaet kommen muessen.
    this.soulsRescued += params.divertedRescues ?? 0;

    this.active = this.active.filter((i) => i.id !== incident.id);

    return {
      incident,
      tier,
      elapsedHours: elapsed,
      souls: incident.souls,
      rescued: tier === 'rescue' ? incident.souls : 0,
      clues: [...clues, ...enRoute],
      reputationDelta,
      beyondWorkingDepth,
    };
  }

  /**
   * Vergibt Hinweise, die zur Art des Einsatzes passen.
   *
   * Die Beats eines Akts bilden einen Pool statt einer festen Reihenfolge:
   * Entnommen wird nur, was die erreichte Stufe ueberhaupt hergeben kann. Ein
   * Akt schliesst erst, wenn er vollstaendig ist — wer nur rettet, sammelt
   * Zeugenaussagen und kommt irgendwann nicht weiter.
   *
   * Die Kohaerenz-Garantie bleibt davon unberuehrt: Sie ist ueber die *Mengen*
   * je Akt definiert, nicht ueber die Reihenfolge innerhalb eines Akts.
   */
  private awardClues(
    count: number,
    incident: Incident,
    tier: OutcomeTier | null,
    explicitKinds?: readonly string[],
  ): CollectedClue[] {
    const awarded: CollectedClue[] = [];
    const kinds = explicitKinds ?? (tier === null ? [] : EVIDENCE_KINDS_PER_TIER[tier]);
    if (kinds.length === 0) return awarded;

    for (let i = 0; i < count; i++) {
      const beat = this.nextMatchingBeat(kinds);
      if (beat === null) break;

      const clue = this.personalise(toCollectedClue(beat, incident.vesselName));
      this.ledger.add(clue);
      awarded.push(clue);
      this.collected.add(beat.beat.index);
    }

    return awarded;
  }

  /**
   * Ersetzt die Spur der Vermissten in Akt III durch eine Fassung, die den
   * eigenen Verlust benennt.
   *
   * Damit faellt der harte Beleg, der den Fall entscheidet, mit der Antwort
   * auf die persoenliche Frage zusammen — die Aufklaerung ist kein Sieg,
   * sondern eine Rechnung, die aufgeht.
   */
  private personalise(clue: CollectedClue): CollectedClue {
    if (clue.slot !== 'missing_person_trace' || clue.act !== 3) return clue;

    return {
      ...clue,
      textKey: `clue.${clue.mystery}.personal_trace`,
      params: { name: this.character.lostName },
    };
  }

  /** Vergibt Hinweise einer bestimmten Fundart, etwa von unterwegs. */
  private awardCluesOfKind(
    count: number,
    incident: Incident,
    kind: string,
  ): CollectedClue[] {
    return this.awardClues(count, incident, null, [kind]);
  }

  /** Der naechste offene Beat des laufenden Akts, dessen Slot passt. */
  private nextMatchingBeat(kinds: readonly string[]): CaseBeat | null {
    const act = this.currentAct;

    for (const beat of this.mysteryCase.beats) {
      if (beat.beat.act !== act) continue;
      if (this.collected.has(beat.beat.index)) continue;
      if (!kinds.includes(beat.beat.slot)) continue;
      return beat;
    }

    return null;
  }

  /**
   * Welche Beweisarten der laufende Akt noch braucht — damit die Basis
   * anzeigen kann, wonach man eigentlich suchen muesste.
   */
  get outstandingEvidenceKinds(): string[] {
    const act = this.currentAct;
    const open = this.mysteryCase.beats.filter(
      (b) => b.beat.act === act && !this.collected.has(b.beat.index),
    );
    return [...new Set(open.map((b) => b.beat.slot))];
  }

  // ------------------------------------------------------------- Fahrten

  /** Alle Fahrzeuge, die gerade unterwegs sind. */
  get activeMissions(): readonly ActiveMission[] {
    return Object.values(this.missions).filter((m): m is ActiveMission => m !== undefined);
  }

  missionOf(vesselId: VesselId): ActiveMission | null {
    return this.missions[vesselId] ?? null;
  }

  isUnderway(vesselId: VesselId): boolean {
    return this.missions[vesselId] !== undefined;
  }

  /**
   * Was ein Kurswechsel dieses Fahrzeugs kosten wuerde.
   *
   * `null`, wenn es ohnehin stillliegt. Sonst der Vorfall, der aufgegeben
   * wird, und die Stunden, die schon in ihn geflossen sind — die sind weg,
   * ganz gleich wohin man danach faehrt.
   */
  abandonmentCost(vesselId: VesselId): { incident: Incident | null; spentHours: number } | null {
    const mission = this.missions[vesselId];
    if (mission === undefined) return null;

    return {
      incident: mission.incident,
      spentHours: mission.voyage.elapsedHours,
    };
  }

  /**
   * Schickt ein Fahrzeug los.
   *
   * Ein Fahrzeug, das schon faehrt, laesst sich nicht ein zweites Mal
   * losschicken — es kann nur seinen Kurs wechseln, und das ist eine eigene
   * Sache.
   */
  dispatch(params: {
    vesselId: VesselId;
    incident: Incident | null;
    speed: SpeedSetting;
    homebound?: boolean;
  }): ActiveMission {
    const { vesselId, incident, speed } = params;
    const homebound = params.homebound ?? incident === null;

    const profile = vesselId === 'ship' ? RESEARCH_SHIP : SUBMARINE;

    /*
     * Wo das Fahrzeug *jetzt* steht, nicht wo es zuletzt festgemacht hat.
     *
     * Faehrt es schon, ist die gespeicherte Position die des letzten
     * Einsatzorts — ein Kurswechsel auf halber Strecke haette den Kurs von
     * dort aus gerechnet und das Schiff damit zurueckversetzt. Ebenso bei
     * Treibstoff und Rumpf: Was die Fahrt bis hierher gekostet hat, ist
     * ausgegeben und darf nicht zurueckfallen.
     */
    const running = this.missions[vesselId];
    const from = running?.voyage.state.position ?? this.positionOf(vesselId);
    const condition = running
      ? { fuel: running.voyage.state.fuel, hull: running.voyage.state.hull }
      : this.vessels[vesselId];

    // Die neue Position gilt ab sofort auch fuer den Rest des Spiels — sonst
    // rechnete die Notrufliste weiter mit dem alten Standort.
    this.vessels[vesselId] = {
      fuel: condition.fuel,
      hull: condition.hull,
      position: { ...from },
    };

    const to = homebound ? HOME_BASE.position : (incident as Incident).position;

    const route = homebound
      ? this.routeBetween(from, HOME_BASE.position, profile.draughtMetres)
      : this.routeTo(incident as Incident, profile.draughtMetres, from);

    const voyage = new Voyage({
      route,
      profile,
      from,
      to,
      weather: this.weather,
      startHours: this.clock,
      fuel: condition.fuel,
      hull: condition.hull,
      speed,
    });

    // Ereignisse haengen am Seed und am Ziel, damit ein Neuladen dieselbe
    // Fahrt ergibt.
    const label = homebound ? `home-${Math.round(this.clock)}` : (incident as Incident).id;

    const mission: ActiveMission = {
      vesselId,
      incident,
      homebound,
      voyage,
      plannedEvents: planVoyageEvents(
        Rng.fromSeed(`${this.seed}::${vesselId}::${label}`),
        route.lengthNm,
      ),
      nextEventIndex: 0,
      divertedFindings: 0,
      divertedRescues: 0,
    };

    this.missions[vesselId] = mission;
    return mission;
  }

  /**
   * Laesst alle laufenden Fahrten um `hours` weiterfahren.
   *
   * Die Kampagnenuhr folgt der *weitesten* Fahrt: Sonst liefe die Zeit fuer
   * das langsamere Fahrzeug anders als fuer das schnellere, und zwei
   * gleichzeitige Einsaetze waeren nicht mehr vergleichbar.
   *
   * Zurueck kommt, was Aufmerksamkeit braucht — angekommene Fahrzeuge und
   * anstehende Ereignisse. Wer nichts davon behandelt, faehrt weiter.
   */
  advanceMissions(hours: number): {
    arrived: readonly ActiveMission[];
    events: readonly { mission: ActiveMission; event: VoyageEvent }[];
  } {
    const arrived: ActiveMission[] = [];
    const events: { mission: ActiveMission; event: VoyageEvent }[] = [];

    for (const mission of this.activeMissions) {
      const state = mission.voyage.state;
      if (state.arrived || state.stranded) {
        arrived.push(mission);
        continue;
      }

      mission.voyage.advance(hours);

      const next = mission.plannedEvents[mission.nextEventIndex];
      if (next !== undefined) {
        const total = mission.voyage.route.lengthNm;
        const covered = total - mission.voyage.state.remainingNm;
        if (total > 0 && covered / total >= next.atProgress) {
          mission.nextEventIndex++;
          events.push({ mission, event: next });
        }
      }

      const after = mission.voyage.state;
      if (after.arrived || after.stranded) arrived.push(mission);
    }

    // Die Uhr folgt der Fahrt, die am weitesten ist.
    let furthest = this.clock;
    for (const mission of this.activeMissions) {
      furthest = Math.max(furthest, mission.voyage.state.clockHours);
    }
    this.advanceTo(furthest);

    return { arrived, events };
  }

  /** Nimmt die Entscheidung eines Transit-Ereignisses entgegen. */
  applyVoyageChoice(
    mission: ActiveMission,
    choice: { delayHours: number; fuelCost: number; hullDamage: number; yieldsEvidence: boolean; soulsRescued: number },
  ): void {
    mission.voyage.spendTime(choice.delayHours, choice.fuelCost, choice.hullDamage);
    if (choice.yieldsEvidence) mission.divertedFindings++;
    mission.divertedRescues += choice.soulsRescued;
  }

  /** Streicht eine Fahrt aus der Liste — nach Ankunft oder Abbruch. */
  clearMission(vesselId: VesselId): void {
    delete this.missions[vesselId];
  }

  /** Nimmt eine These des Spielers entgegen und bewertet sie. */
  accuse(accusation: MysteryId): Verdict {
    const judgement = this.ledger.judge(accusation, this.mysteryCase.truth);
    return {
      accusation,
      truth: this.mysteryCase.truth,
      correct: judgement.correct,
      supported: judgement.supported,
      cluesCollected: this.ledger.size,
      cluesPossible: TOTAL_BEATS,
      rescued: this.soulsRescued,
      lost: this.soulsLost,
      // Nur wer die Spur der Vermissten geborgen hat, erfaehrt auch, was mit
      // der eigenen verlorenen Person geschah.
      personalAnswered: this.ledger.all.some(
        (clue) => clue.slot === 'missing_person_trace' && clue.act === 3,
      ),
    };
  }

  /**
   * Der Spielstand als schlichte Daten.
   *
   * Die Notrufe werden mitgeschrieben statt neu erzeugt: Sie haengen am
   * Fortschritt des Zufallsstroms, und den nachzustellen waere fehleranfaellig.
   * Als reine Daten sind sie ohnehin klein.
   */
  serialise(): CampaignSave {
    return {
      version: SAVE_VERSION,
      seed: this.seed,
      character: this.character,
      clockHours: this.clock,
      collected: [...this.collected],
      reputation: this.reputationTotal,
      missionsFlown: this.missionsFlown,
      soulsRescued: this.soulsRescued,
      soulsLost: this.soulsLost,
      incidentCounter: this.incidentCounter,
      nextSpawnAt: this.nextSpawnAt,
      active: this.active,
      vessels: this.vessels,
      tutorial: { enabled: this.tutorial.enabled, seen: [...this.tutorial.seen] },
    };
  }

  /** Stellt einen Spielstand wieder her. */
  static restore(
    save: CampaignSave,
    bathymetry: Bathymetry,
    coastline: readonly CoastStrand[] = [],
  ): Campaign {
    const campaign = new Campaign({
      seed: save.seed,
      bathymetry,
      character: save.character,
      coastline,
    });

    campaign.clock = save.clockHours;
    campaign.reputationTotal = save.reputation;
    campaign.missionsFlown = save.missionsFlown;
    campaign.soulsRescued = save.soulsRescued;
    campaign.soulsLost = save.soulsLost;
    campaign.incidentCounter = save.incidentCounter;
    campaign.nextSpawnAt = save.nextSpawnAt;
    campaign.active = [...save.active];
    campaign.vessels.ship = { ...save.vessels.ship, position: { ...save.vessels.ship.position } };
    campaign.vessels.submarine = {
      ...save.vessels.submarine,
      position: { ...save.vessels.submarine.position },
    };

    campaign.tutorial.enabled = save.tutorial.enabled;
    campaign.tutorial.seen.length = 0;
    campaign.tutorial.seen.push(...save.tutorial.seen);

    // Die Beweislage aus den Beat-Indizes nachbauen — so bleibt der Spielstand
    // klein und kann nie von den Fall-Daten abweichen.
    campaign.collected.clear();
    for (const index of save.collected) {
      const beat = campaign.mysteryCase.beats[index];
      if (beat === undefined) continue;
      campaign.collected.add(index);
      campaign.ledger.add(campaign.personalise(toCollectedClue(beat, '—')));
    }

    return campaign;
  }

  /**
   * Bringt ein Fahrzeug zur Station zurueck.
   *
   * Die Rueckfahrt ist keine Kulisse: Sie kostet Stunden und Treibstoff, in
   * denen die Uhren aller offenen Notrufe weiterlaufen. Genau deshalb ist
   * Draussenbleiben ueberhaupt eine Ueberlegung wert.
   */
  returnToBase(vesselId: VesselId, params: { arrivalHours: number; fuelLeft: number; hullLeft: number }): void {
    this.advanceTo(params.arrivalHours);
    this.vessels[vesselId] = {
      fuel: params.fuelLeft,
      hull: params.hullLeft,
      position: { ...HOME_BASE.position },
    };
  }

  /**
   * Tankt und repariert — kostet Zeit.
   *
   * Nur an der Station. Auf See gibt es keinen Nachschub, und das ist die
   * Gegenrechnung dafuer, dass man von dort schneller am naechsten Notruf ist.
   */
  resupply(vesselId: VesselId): number {
    if (!this.isAtBase(vesselId)) return 0;

    const profile = vesselId === 'ship' ? RESEARCH_SHIP : SUBMARINE;
    const condition = this.vessels[vesselId];

    const missingFuel = profile.fuelCapacity - condition.fuel;
    const missingHull = 100 - condition.hull;
    // Nachschub und Instandsetzung brauchen Zeit — waehrenddessen laufen die
    // Uhren der offenen Notrufe weiter.
    const base = (missingFuel / profile.fuelCapacity) * 8 + (missingHull / 100) * 20;

    /*
     * Ein Institut, das geliefert hat, bekommt die Werft schneller.
     *
     * Die zweite Wirkung des Ansehens, und die harmlosere: Sie beschleunigt
     * oder verzoegert nur, was ohnehin passiert. Zwischen halber und
     * anderthalbfacher Dauer — genug, um es zu merken, zu wenig, um einen
     * schlechten Lauf unspielbar zu machen.
     */
    const hours = base * this.refitFactor;

    condition.fuel = profile.fuelCapacity;
    condition.hull = 100;
    this.wait(hours);

    return hours;
  }
}
